export type EffectivenessMetricKey = 'firstResponse' | 'response' | 'handling' | 'resolution';
export type EffectivenessAggregation = 'maximum' | 'minimum' | 'average' | 'median';

export const EFFECTIVENESS_METRIC_KEYS: Record<
  EffectivenessMetricKey,
  { label: string; tooltip: string }
> = {
  firstResponse: {
    label: 'analytics.human_agents_effectiveness.first_response_time',
    tooltip: 'analytics.human_agents_effectiveness.first_response_time_tooltip',
  },
  response: {
    label: 'analytics.human_agents_effectiveness.response_time',
    tooltip: 'analytics.human_agents_effectiveness.response_time_tooltip',
  },
  handling: {
    label: 'analytics.human_agents_effectiveness.handling_time',
    tooltip: 'analytics.human_agents_effectiveness.handling_time_tooltip',
  },
  resolution: {
    label: 'analytics.human_agents_effectiveness.resolution_time',
    tooltip: 'analytics.human_agents_effectiveness.resolution_time_tooltip',
  },
};

export const EFFECTIVENESS_AGGREGATION_LABEL_KEYS: Record<EffectivenessAggregation, string> = {
  maximum: 'analytics.human_agents_effectiveness.aggregation.maximum',
  minimum: 'analytics.human_agents_effectiveness.aggregation.minimum',
  average: 'analytics.human_agents_effectiveness.aggregation.average',
  median: 'analytics.human_agents_effectiveness.aggregation.median',
};

export interface EffectivenessTimeseriesPoint {
  date: string;
  // Null on days with no samples: durations are averages, so we keep the gap
  // rather than fabricating a 0 (which would read as an impossible 0s duration).
  value: number | null;
}

const METRIC_FIELD_PREFIX: Record<EffectivenessMetricKey, string> = {
  firstResponse: 'first_response',
  response: 'response',
  handling: 'handling',
  resolution: 'resolution',
};

const AGGREGATION_FIELD_SUFFIX: Record<EffectivenessAggregation, string> = {
  maximum: 'max',
  minimum: 'min',
  average: 'avg',
  median: 'median',
};

export type EffectivenessMetrics = Record<
  `${'first_response' | 'response' | 'handling' | 'resolution'}_${'min' | 'max' | 'avg' | 'median'}`,
  number | null
>;

export const getEffectivenessValue = (
  data: EffectivenessMetrics | null | undefined,
  metric: EffectivenessMetricKey,
  aggregation: EffectivenessAggregation
): number | null => {
  if (!data) {
    return null;
  }
  const key =
    `${METRIC_FIELD_PREFIX[metric]}_${AGGREGATION_FIELD_SUFFIX[aggregation]}` as keyof EffectivenessMetrics;
  return data[key] ?? null;
};
